import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, Shield, Clock } from 'lucide-react';

interface PaymentLoadingOverlayProps {
  isVisible: boolean;
}

export const PaymentLoadingOverlay: React.FC<PaymentLoadingOverlayProps> = ({ isVisible }) => {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="bg-background rounded-2xl p-8 max-w-md w-full shadow-2xl border"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className="text-center space-y-6">
              {/* Animated Icon */}
              <div className="relative">
                <motion.div
                  className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center"
                  animate={{ 
                    boxShadow: [
                      "0 0 0 0 rgba(var(--primary), 0.2)",
                      "0 0 0 20px rgba(var(--primary), 0)",
                      "0 0 0 0 rgba(var(--primary), 0)"
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <CreditCard className="h-8 w-8 text-primary" />
                </motion.div>
              </div>

              {/* Title */}
              <div>
                <h3 className="text-xl font-semibold mb-2">
                  Procesando tu pago
                </h3>
                <p className="text-muted-foreground text-sm">
                  Te estamos redirigiendo a Stripe para completar tu pago de forma segura
                </p>
              </div>

              {/* Loading Steps */}
              <div className="space-y-3">
                <motion.div 
                  className="flex items-center justify-center space-x-3 p-3 bg-muted/20 rounded-lg"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Shield className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Verificando datos de seguridad</span>
                  <motion.div
                    className="w-2 h-2 bg-green-600 rounded-full"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                </motion.div>

                <motion.div 
                  className="flex items-center justify-center space-x-3 p-3 bg-muted/20 rounded-lg"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 }}
                >
                  <CreditCard className="h-4 w-4 text-blue-600" />
                  <span className="text-sm">Conectando con Stripe</span>
                  <motion.div
                    className="w-2 h-2 bg-blue-600 rounded-full"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                  />
                </motion.div>

                <motion.div 
                  className="flex items-center justify-center space-x-3 p-3 bg-muted/20 rounded-lg"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.5 }}
                >
                  <Clock className="h-4 w-4 text-orange-600" />
                  <span className="text-sm">Preparando la sesión de pago</span>
                  <motion.div
                    className="w-2 h-2 bg-orange-600 rounded-full"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                  />
                </motion.div>
              </div>

              {/* Security Notice */}
              <div className="bg-green-50 dark:bg-green-950/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-xs text-green-700 dark:text-green-300">
                  <Shield className="h-3 w-3 inline mr-1" />
                  Tu información está protegida con encriptación SSL de 256 bits
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};